import { Box, Checkbox, Group, Text, Tooltip, VisuallyHidden } from '@mantine/core';
import { IconCertificate, IconLock, IconMail, IconMailbox } from '@tabler/icons-react';
import { tKeys, useTranslate } from 'locales/index';
import { type ReactNode, useId } from 'react';
import { isNonNullish } from 'remeda';
import type { ReminderDocumentType } from '../api/preview-reminder-request';

export type ReminderChannelsValue = {
  sendEmail: boolean;
  sendPost: boolean;
  sendRegisteredPost: boolean;
};

type ReminderChannelsProps = {
  value: ReminderChannelsValue;
  onChange: (next: ReminderChannelsValue) => void;
  documentType: ReminderDocumentType;
};

const ChannelCard = ({
  checked,
  disabled,
  icon,
  label,
  lockedReason,
  onToggle,
}: {
  checked: boolean;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  // When set, the channel is imposed by the document type: a padlock replaces the
  // plain disabled look so it reads as "always on" rather than "unavailable".
  lockedReason?: string;
  onToggle: () => void;
}) => {
  const lockedReasonId = useId();
  const card = (
    <Checkbox.Card
      checked={checked}
      disabled={disabled}
      onClick={onToggle}
      aria-label={label}
      aria-describedby={isNonNullish(lockedReason) ? lockedReasonId : undefined}
      radius="md"
      p="sm"
      style={{ flex: 1 }}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        {icon}
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Group gap={6} ml="auto" wrap="nowrap">
          {isNonNullish(lockedReason) && (
            <>
              <IconLock size={14} stroke={1.5} />
              <VisuallyHidden id={lockedReasonId}>{lockedReason}</VisuallyHidden>
            </>
          )}
          <Checkbox.Indicator />
        </Group>
      </Group>
    </Checkbox.Card>
  );

  if (!isNonNullish(lockedReason)) return card;

  // Wrapper element: the disabled card swallows pointer events, so the tooltip
  // needs a target of its own to pick the hover up.
  return (
    <Tooltip label={lockedReason}>
      <Box style={{ display: 'flex', flex: 1 }}>{card}</Box>
    </Tooltip>
  );
};

export const ReminderChannels = ({ value, onChange, documentType }: ReminderChannelsProps) => {
  const t = useTranslate();
  const patch = (change: Partial<ReminderChannelsValue>) => onChange({ ...value, ...change });

  const emailCard = (
    <ChannelCard
      checked={value.sendEmail}
      icon={<IconMail size={20} />}
      label={t(tKeys.EMAIL)}
      onToggle={() => patch({ sendEmail: !value.sendEmail })}
    />
  );

  if (documentType === 'formal_notice') {
    return (
      <Group gap="xs" grow align="stretch" wrap="nowrap">
        {emailCard}
        <ChannelCard
          checked={value.sendRegisteredPost}
          disabled
          lockedReason={t(tKeys.MODULES.AT_RISK.REGISTERED_POST_MANDATORY)}
          icon={<IconCertificate size={20} />}
          label={t(tKeys.REMINDER_LIST.FILTERS.SENT_MEDIA.REGISTERED_POST)}
          onToggle={() => {}}
        />
      </Group>
    );
  }

  return (
    <Group gap="xs" grow align="stretch" wrap="nowrap">
      {emailCard}
      <ChannelCard
        checked={value.sendPost}
        disabled={value.sendRegisteredPost}
        icon={<IconMailbox size={20} />}
        label={t(tKeys.POST)}
        onToggle={() => patch({ sendPost: !value.sendPost })}
      />
      <ChannelCard
        checked={value.sendRegisteredPost}
        disabled={value.sendPost}
        icon={<IconCertificate size={20} />}
        label={t(tKeys.REMINDER_LIST.FILTERS.SENT_MEDIA.REGISTERED_POST)}
        onToggle={() => patch({ sendRegisteredPost: !value.sendRegisteredPost })}
      />
    </Group>
  );
};
